/**
 * CGM-OPERATIONAL-CONTROL-R3 — semantic pump command machine (section 7).
 *
 * Reuses the semantic pump endpoint (POST /iot/pumps/{pump_id}/command) with
 * the same discipline as the IoTNodes pump machine:
 *   - role permission + binding.command_authorized gate
 *   - confirmation dialog → zero POST until confirmed
 *   - stable idempotency key, one intent = one POST
 *   - expected-node identity header (relay node) to close the GET→POST re-bind race
 *   - 403/409/network ambiguity → fail closed, no automatic retry
 *   - HTTP success is NOT physical proof (the backend interlock + observation
 *     remain authoritative)
 *
 * For START on a MANUAL_OPERATOR_CONFIRMATION_REQUIRED pump (Luc / Escales), a
 * hydraulic confirmation is required first — that confirmation is NOT a bypass
 * for commissioning / actuation / relay binding / RBAC.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, iotApi, type Command, type PumpBindingView } from '../lib/iotApi';
import { canIssuePhysicalCommands } from '../auth/cognito';
import { isBusyStatus, isTerminalCommandStatus } from '../lib/valveCommandSafety';

export interface PendingPumpConfirmation {
  pump: PumpBindingView;
  action: 'start' | 'stop';
  idempotencyKey: string;
  requiresHydraulicConfirmation: boolean;
}

export type PumpCommandOutcome = 'forbidden' | 'conflict' | 'uncertain' | null;

let idempotencySeq = 0;
const generateIdempotencyKey = (): string => {
  idempotencySeq += 1;
  const rand = Math.random().toString(36).slice(2, 10);
  return `pump-${Date.now().toString(36)}-${idempotencySeq.toString(36)}-${rand}`;
};

const POLL_MS = 5000;
const MAX_POLLS = 12;

export interface UsePumpCommandResult {
  pending: PendingPumpConfirmation | null;
  statusFor: (pumpId: string) => string | undefined;
  outcomeFor: (pumpId: string) => PumpCommandOutcome;
  requestCommand: (pump: PumpBindingView, action: 'start' | 'stop') => void;
  cancelConfirmation: () => void;
  confirmCommand: () => Promise<void>;
  canRecheck: (pumpId: string) => boolean;
  recheckCommand: (pump: PumpBindingView) => void;
}

export function usePumpCommand(role: string | null | undefined): UsePumpCommandResult {
  const [pending, setPending] = useState<PendingPumpConfirmation | null>(null);
  const [status, setStatus] = useState<Record<string, string>>({});
  const [outcome, setOutcome] = useState<Record<string, PumpCommandOutcome>>({});
  const pendingRef = useRef<PendingPumpConfirmation | null>(null);
  const statusRef = useRef<Record<string, string>>({});
  const activeCommandRef = useRef<Record<string, string>>({});
  const pollTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  const pollCountRef = useRef<Record<string, number>>({});
  const readInFlightRef = useRef<Set<string>>(new Set());

  const canControl = canIssuePhysicalCommands(role);

  const setStatusFor = useCallback((pumpId: string, st: string | null, commandId?: string) => {
    const next = { ...statusRef.current };
    const nextCmd = { ...activeCommandRef.current };
    if (st === null) {
      delete next[pumpId];
      delete nextCmd[pumpId];
    } else {
      next[pumpId] = st;
      if (commandId) nextCmd[pumpId] = commandId;
    }
    statusRef.current = next;
    activeCommandRef.current = nextCmd;
    setStatus(next);
  }, []);

  useEffect(() => {
    const timers = pollTimersRef.current;
    return () => {
      Object.values(timers).forEach((t) => clearTimeout(t));
    };
  }, []);

  const pollExactCommand = useCallback((pump: PumpBindingView, commandId: string) => {
    const pumpId = pump.pump_id;
    if (readInFlightRef.current.has(pumpId)) return;
    const count = (pollCountRef.current[pumpId] ?? 0) + 1;
    pollCountRef.current[pumpId] = count;
    if (count > MAX_POLLS) {
      setStatusFor(pumpId, 'UNCERTAIN', commandId);
      return;
    }
    readInFlightRef.current.add(pumpId);
    iotApi
      // Batch node_ids only exposes valve_control. The single-node route also
      // returns relay_control (motors); match the exact command below. If it
      // falls outside this bounded page, keep UNCERTAIN rather than guessing.
      .getJson<Command[]>(`/iot/commands?node_id=${encodeURIComponent(pump.relay_node_id)}&limit=500`)
      .then((rows) => {
        if (activeCommandRef.current[pumpId] !== commandId) return;
        const list = (Array.isArray(rows) ? (rows as Command[]) : []).map((c) => ({
          ...c,
          id: c.command_id ?? c.id,
        }));
        const cmd = list.find((c) => c.id === commandId && c.node_id === pump.relay_node_id
          && c.channel_id === pump.relay_channel);
        if (!cmd) {
          setStatusFor(pumpId, 'UNCERTAIN');
          return;
        }
        const st = typeof cmd.status === 'string' ? cmd.status : 'UNCERTAIN';
        setStatusFor(pumpId, st, commandId);
        if (st === 'OBSERVED_SUCCESS' || st === 'OBSERVED_FAILED' || st === 'TIMEOUT') {
          setOutcome(prev => ({ ...prev, [pumpId]: null }));
        }
        if (!isTerminalCommandStatus(st)) {
          pollTimersRef.current[pumpId] = setTimeout(() => pollExactCommand(pump, commandId), POLL_MS);
        }
      })
      .catch(() => {
        if (activeCommandRef.current[pumpId] !== commandId) return;
        setStatusFor(pumpId, 'UNCERTAIN');
      })
      .finally(() => { readInFlightRef.current.delete(pumpId); });
  }, [setStatusFor]);

  const requestCommand = useCallback(
    (pump: PumpBindingView, action: 'start' | 'stop') => {
      if (!pump.command_authorized || !canControl || isBusyStatus(statusRef.current[pump.pump_id])) return;
      const requiresHydraulicConfirmation =
        action === 'start'
        && pump.startup_permissive?.mode === 'MANUAL_OPERATOR_CONFIRMATION_REQUIRED';
      const confirmation: PendingPumpConfirmation = {
        pump,
        action,
        idempotencyKey: generateIdempotencyKey(),
        requiresHydraulicConfirmation,
      };
      pendingRef.current = confirmation;
      setPending(confirmation);
    },
    [canControl],
  );

  const cancelConfirmation = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  const confirmCommand = useCallback(async () => {
    const confirmation = pendingRef.current;
    if (!confirmation) return;
    const { pump, action, idempotencyKey, requiresHydraulicConfirmation } = confirmation;
    pendingRef.current = null;
    setPending(null);

    if (!canIssuePhysicalCommands(role)) {
      setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'forbidden' }));
      return;
    }
    if (!pump.command_authorized) {
      setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'forbidden' }));
      return;
    }
    if (isBusyStatus(statusRef.current[pump.pump_id])) return;

    delete activeCommandRef.current[pump.pump_id];
    setOutcome(prev => ({ ...prev, [pump.pump_id]: null }));
    setStatusFor(pump.pump_id, 'REQUESTED');

    try {
      const res = await iotApi.post(
        `/iot/pumps/${encodeURIComponent(pump.pump_id)}/command`,
        {
          action,
          ...(requiresHydraulicConfirmation
            ? { operator_hydraulic_confirmation: true }
            : {}),
        },
        idempotencyKey,
        {
          'X-AgriSafe-Expected-Node-ID': pump.relay_node_id,
          'X-AgriSafe-Expected-Relay-Channel': pump.relay_channel ?? '',
        },
      );
      const commandId = typeof res?.command_id === 'string' && res.command_id.trim().length > 0
        ? res.command_id
        : undefined;

      if (!commandId) {
        setStatusFor(pump.pump_id, 'UNCERTAIN');
        setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'uncertain' }));
        return;
      }

      const st = typeof res?.status === 'string' ? res.status : 'REQUESTED';
      setStatusFor(pump.pump_id, st, commandId);
      if (!isTerminalCommandStatus(st)) {
        pollCountRef.current[pump.pump_id] = 0;
        pollExactCommand(pump, commandId);
      }
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.semanticCode === 'conflict' || err.httpStatus === 409) {
          setStatusFor(pump.pump_id, 'UNCERTAIN');
          setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'conflict' }));
        } else if (err.semanticCode === 'forbidden' || err.httpStatus === 403) {
          setStatusFor(pump.pump_id, null);
          setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'forbidden' }));
        } else {
          setStatusFor(pump.pump_id, 'UNCERTAIN');
          setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'uncertain' }));
        }
      } else {
        setStatusFor(pump.pump_id, 'UNCERTAIN');
        setOutcome((prev) => ({ ...prev, [pump.pump_id]: 'uncertain' }));
      }
    }
  }, [role, setStatusFor, pollExactCommand]);

  return {
    pending,
    statusFor: (pumpId: string) => status[pumpId],
    outcomeFor: (pumpId: string) => outcome[pumpId] ?? null,
    requestCommand,
    cancelConfirmation,
    confirmCommand,
    canRecheck: (pumpId) => Boolean(activeCommandRef.current[pumpId])
      && status[pumpId] === 'UNCERTAIN',
    recheckCommand: (pump) => {
      const id = activeCommandRef.current[pump.pump_id];
      if (!id || !canControl || readInFlightRef.current.has(pump.pump_id)) return;
      clearTimeout(pollTimersRef.current[pump.pump_id]);
      pollCountRef.current[pump.pump_id] = 0;
      pollExactCommand(pump, id);
    },
  };
}
